'use client'
import * as React from 'react'
import { m, LazyMotion, domAnimation, useAnimationControls } from 'framer-motion'
import { tw } from 'utils/classnames'
import { SearchIcon } from 'primitives/icons'
import { useOnClickOutside } from 'hooks/use-on-click-outside'

export interface SearchInputProps extends PropsWithOptionalChildren<
  React.InputHTMLAttributes<HTMLInputElement>
> {
  showResults?: boolean
  setShowResults?: (value: boolean) => void
  containerClassName?: string
  resultsClassName?: string
  InputIcon?: React.ReactNode
  InputButton?: React.ReactNode
  withoutResults?: boolean
  routeToTemplate?: string
  selectedCategory?: string
  setSelectedCategory?: React.Dispatch<React.SetStateAction<string>>
  isIconRight?: boolean
}

export function SearchInputIcon({ className = '', ...rest }: React.HTMLAttributes<SVGElement>) {
  return <SearchIcon className={tw(`h-6 w-6 text-gray-400 ${className}`)} {...rest} />
}

export function SearchInputButton({ className = '', ...rest }: React.ComponentProps<'button'>) {
  return (
    <button type="button" className="text-16 sm:text-14 flex gap-2 pr-1 text-gray-400" {...rest}>
      Search
    </button>
  )
}

const variants = {
  expanded: { opacity: 1, height: 'auto', transition: { duration: 0.1 } },
  collapsed: { opacity: 0, height: 0, transition: { duration: 0.1 } },
}

export function SearchInput({
  children,
  showResults = false,
  setShowResults,
  containerClassName,
  className,
  resultsClassName,
  InputIcon = <SearchInputIcon />,
  InputButton = <SearchInputButton />,
  withoutResults = false,
  isIconRight = false,
  routeToTemplate: _routeToTemplate,
  selectedCategory: _selectedCategory,
  setSelectedCategory: _setSelectedCategory,
  ...inputProps
}: SearchInputProps) {
  const controls = useAnimationControls()
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    controls.start(showResults ? 'expanded' : 'collapsed')
  }, [showResults, controls])

  useOnClickOutside(ref, () => {
    if (!setShowResults) {
      return
    }

    setShowResults(false)
  })

  return (
    <div ref={ref} className="relative w-full">
      <label
        className={tw(
          showResults && children ? 'rounded-t-2' : 'rounded-2',
          'bg-service-white flex items-center justify-between gap-2 self-stretch border border-gray-300 p-4',
          containerClassName,
        )}
      >
        {!isIconRight ? InputIcon : null}
        <input
          className={tw(`w-full text-gray-600 placeholder-gray-400 focus:outline-none`, className)}
          {...inputProps}
        />
        {isIconRight ? InputIcon : null}
      </label>
      <LazyMotion features={domAnimation}>
        <m.div
          initial={showResults ? 'expanded' : 'collapsed'}
          animate={controls}
          variants={variants}
          className={tw(
            // oxlint-disable-next-line no-negated-condition
            !showResults ? 'z-0 hidden' : 'z-10',
            !withoutResults &&
              'rounded-b-2 bg-service-white absolute flex w-full flex-col self-stretch border border-t-0 border-gray-700 p-2',
            resultsClassName,
          )}
        >
          {children}
        </m.div>
      </LazyMotion>
    </div>
  )
}

export function SearchInputFallback({
  children: _children,
  showResults: _showResults,
  setShowResults: _setShowResults,
  containerClassName,
  className,
  resultsClassName: _resultsClassName,
  InputIcon = <SearchInputIcon />,
  InputButton: _InputButton,
  withoutResults: _withoutResults,
  routeToTemplate: _routeToTemplate,
  selectedCategory: _selectedCategory,
  setSelectedCategory: _setSelectedCategory,
  isIconRight: _isIconRight,
  ...inputProps
}: SearchInputProps) {
  return (
    <div
      className={tw(
        'rounded-2 bg-service-white flex w-full items-center justify-between gap-2 self-stretch border border-gray-300 p-4',
        containerClassName,
      )}
    >
      {InputIcon}
      <input
        className={tw(`w-full text-gray-600 placeholder-gray-400 focus:outline-none`, className)}
        {...inputProps}
      />
    </div>
  )
}
